import { createSelector } from 'reselect'

import {
  DATE_VALUES,
  getParsedDateUntilToday,
  getRangeFromDateFilter,
  HOME_DASHBOARD_DATE_FILTERS,
} from 'utils/dateUtils'
import { allPropertiesFalse } from 'utils/helpers'
import { checkPerm } from 'utils/permManager'

import {
  selectActualPermissions,
  selectIsMaster,
  selectSelfPermissions,
} from 'containers/App/selectors'

// `dashboard` is injected only while a Dashboard route is mounted
// (renderComponentWithDataLayer(Dashboard, ['Dashboard']) in RootContainer)
// and dropped again once another route takes over; in-flight
// subscriptions/epics can still read state in that window, so slice access
// must be null-safe (see AwarenessCenter/Campaigns/selectors.js precedent).
const EMPTY_OBJ = {}
const selectHomeSelf = (state) => state.dashboard?.home?.self ?? EMPTY_OBJ

export const selectDateFilter = (state) => selectHomeSelf(state).dateFilter ?? DATE_VALUES.WEEK

const selectWithoutScanback = (state) => selectHomeSelf(state).withoutScanback ?? false

const selectDateRangeDummy = (state) => selectHomeSelf(state).feDateRange

const selectThreatTypes = (state) => selectHomeSelf(state).threatTypes ?? null

const selectThreatTypesLoader = (state) => selectHomeSelf(state).threatTypesLoader ?? false

const selectDateRange = createSelector(
  selectDateRangeDummy,
  selectDateFilter,
  (feDateRange, dateFilter) =>
    feDateRange || getRangeFromDateFilter(dateFilter, false, HOME_DASHBOARD_DATE_FILTERS)
)

const selectParsedDate = (state) => {
  const currentDateFilter = selectDateFilter(state)
  return getParsedDateUntilToday(currentDateFilter)
}

// Backend truth (webapp/views/unclassified_widget.py unclassified_widget_view):
// `company.has_perms("profiles.irontraps_viewer", user) or is_master(request)`.
// Mirror that requirement exactly — checking a wider IronTraps-adjacent role set
// (irontraps_action, manage_irontraps, etc.) would let a user without
// irontraps_viewer pass this gate and fire a request the backend then 403s.
// (Production seeding — campaigns/migrations_utils/initial_migration.py — gives
// RC Operators irontraps_viewer; the test below uses a role that genuinely lacks
// it, not RC Operators.) Admins always also carry irontraps_viewer, so checking
// it alone still covers them. is_master(request) is an independent OR-branch on
// the backend, so a master user without irontraps_viewer must still pass here.
const selectIncidentsWidgetsVisibility = createSelector(
  selectSelfPermissions,
  selectIsMaster,
  (selfPermissions, isMaster) => isMaster || checkPerm(selfPermissions, ['irontraps_viewer'])
)

const selectHasIncidentWidgetAccess = createSelector(
  (state) => selectActualPermissions(state, allPropertiesFalse),
  selectIsMaster,
  (actualPermissions, isMaster) => isMaster || checkPerm(actualPermissions, ['irontraps_viewer'])
)

// Backend truth (elastic/validators.py validate_request_permissions, used by the
// dashboard-insights-* views): the user must hold `irontraps_viewer` on the
// ACTUAL (swapped-in) company, with no is_master branch. Do not reuse
// selectHasIncidentWidgetAccess here: its `isMaster ||` would let a master
// without the permission fire a request the backend 403s.
const selectHasInsightWidgetAccess = (state) =>
  checkPerm(selectActualPermissions(state, allPropertiesFalse), ['irontraps_viewer'])

export {
  selectHasInsightWidgetAccess,
  selectDateRange,
  selectParsedDate,
  selectWithoutScanback,
  selectIncidentsWidgetsVisibility,
  selectHasIncidentWidgetAccess,
  selectThreatTypes,
  selectThreatTypesLoader,
}
